import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _dec2, _class, _descriptor, _descriptor2, _descriptor3;
import { action, computed, observable } from 'mobx';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import CorrelationCollection from './CorrelationCollection';
import CorrelationStatisticsModel from './CorrelationStatisticsModel';
import { CORRELATIONS_PER_PAGE_DEFAULT, CORRELATION_PERMISSIONS, DEFAULT_CORRELATION_FILTERS, DEFAULT_CORRELATION_SORT } from './constants';
import { activeRange } from './filters';
import { deriveGranularity, granularityChoices } from './granularity';
/** Drops unset values and coerces the dates the filter sidebar hands over as moments. */
function normalize(values) {
  const next = omitBy({
    ...values,
    startDate: values.startDate ? Number(values.startDate) : undefined,
    endDate: values.endDate ? Number(values.endDate) : undefined,
    createdFrom: values.createdFrom ? Number(values.createdFrom) : undefined,
    createdTo: values.createdTo ? Number(values.createdTo) : undefined
  }, value => value === undefined || value === null || value === '' || Array.isArray(value) && value.length === 0);

  // The sidebar fills in dates alongside a lookback it restores from the URL; the lookback wins, so the window
  // keeps moving up to now.
  if (next.lookback) {
    delete next.startDate;
    delete next.endDate;
  }

  // A timeline created range is drawn in, and pins, the window it selects; it lapses once the window changes.
  if (next.createdFrom !== next.startDate || next.createdTo !== next.endDate) {
    delete next.createdFrom;
    delete next.createdTo;
  }
  return next;
}
export let CorrelationsStore = (_dec = observable.ref, _dec2 = observable.ref, _class = class CorrelationsStore {
  constructor() {
    /** Set by `$store` for every store. */
    this.store = void 0;
    /** The correlations list's filters, as its filter sidebar edits them and the URL hash stores them. */
    _initializerDefineProperty(this, "filters", _descriptor, this);
    /** Pinned when the filters change or the list refreshes, so the list, statistics, and timeline share it. */
    _initializerDefineProperty(this, "window", _descriptor2, this);
    /** The timeline's chosen bucket size; unset picks the finest the window can carry. */
    _initializerDefineProperty(this, "granularityValue", _descriptor3, this);
    /** The time range from before the first timeline brush, restored when the brush is cleared. */
    this.preBrushRange = null;
    this.collection = new CorrelationCollection([], {
      sortState: DEFAULT_CORRELATION_SORT
    });
    this.statistics = new CorrelationStatisticsModel({
      id: 'filtered'
    });
    /** Counts over the time scope only, so ticking one facet never zeroes out the others. */
    this.facetStatistics = new CorrelationStatisticsModel({
      id: 'facets'
    });
    this.collection.setPageState({
      perPage: CORRELATIONS_PER_PAGE_DEFAULT
    });
  }

  /** Can ack and un-ack correlations. */
  get canAck() {
    return Boolean(this.store?.$auth.hasRbacPermissions([CORRELATION_PERMISSIONS.ack]));
  }

  /** Can clear, merge, and remove alerts from correlations. */
  get canClear() {
    return Boolean(this.store?.$auth.hasRbacPermissions([CORRELATION_PERMISSIONS.clear]));
  }
  get granularity() {
    const spanMs = this.window.end - this.window.start;
    return granularityChoices(spanMs).find(entry => entry.value === this.granularityValue) ?? deriveGranularity(spanMs);
  }

  /** The filters with their time range pinned to `window`. */
  get query() {
    return {
      ...this.filters,
      lookback: undefined,
      startDate: this.window.start,
      endDate: this.window.end
    };
  }

  /** The range selected on the timeline, if any. */
  get createdRange() {
    const {
      createdFrom,
      createdTo
    } = this.filters;
    return createdFrom && createdTo ? {
      start: createdFrom,
      end: createdTo
    } : null;
  }

  /** The list page's first load, from the filters it restored from the URL (or the defaults). */
  loadList(values) {
    this.filters = normalize(values);
    this.window = activeRange(this.filters);
    return this.load();
  }

  /** Applies the filter sidebar's values and reloads, unless nothing changed. */
  setFilters(values) {
    const next = normalize(values);
    if (isEqual(next, this.filters)) {
      return Promise.resolve();
    }
    this.filters = next;
    this.window = activeRange(next);
    return this.load();
  }

  /** Reloads the list and its statistics, with a lookback window moved up to now. */
  refresh() {
    this.window = activeRange(this.filters);
    return this.load({
      refresh: true
    });
  }

  /** Fetches the page the paginator has moved the collection to. */
  fetchPage({
    refetchTotals = false
  } = {}) {
    if (refetchTotals) {
      this.applyTotals();
    }
    return this.collection.fetch().catch(() => null);
  }
  setGranularity(value) {
    this.granularityValue = value;
    return this.loadStatistics();
  }

  /** Reloads the statistics after correlations change underneath them (acked, cleared, merged). */
  loadStatistics() {
    const {
      granularity,
      window,
      filters
    } = this;
    const {
      createdFrom,
      createdTo
    } = filters;
    return Promise.all([this.statistics.fetch({
      filter: this.query,
      granularity
    }).then(() => this.applyTotals()), this.facetStatistics.fetch({
      filter: {
        startDate: window.start,
        endDate: window.end,
        createdFrom,
        createdTo
      },
      granularity
    })]).catch(() => null);
  }

  /**
   * The filters a timeline brush selects: the brushed range as both the window and the created range. Clearing
   * the brush restores the time range from before the first brush.
   */
  brushedFilters(range) {
    const {
      filters
    } = this;
    if (range) {
      this.preBrushRange ??= {
        lookback: filters.lookback,
        startDate: filters.startDate,
        endDate: filters.endDate
      };
      return {
        ...filters,
        lookback: undefined,
        startDate: range.start,
        endDate: range.end,
        createdFrom: range.start,
        createdTo: range.end
      };
    }
    const {
      lookback,
      startDate,
      endDate
    } = this.preBrushRange ?? DEFAULT_CORRELATION_FILTERS;
    this.preBrushRange = null;
    return {
      ...filters,
      lookback,
      startDate,
      endDate,
      createdFrom: undefined,
      createdTo: undefined
    };
  }
  resetList() {
    this.filters = DEFAULT_CORRELATION_FILTERS;
    this.window = activeRange(DEFAULT_CORRELATION_FILTERS);
    this.granularityValue = null;
    this.preBrushRange = null;
    this.collection.groupBy = null;
    this.collection.sortState = {
      ...DEFAULT_CORRELATION_SORT
    };
    this.collection.clearSelection();
    this.collection.set([]);
    this.collection.lastFetched = null;
    this.collection.resetPageState();
  }

  // Here and in `loadStatistics`/`fetchPage`, failures are kept on the collection and statistics models, which the
  // page renders, so the promises are settled rather than left to reject unhandled.
  load({
    refresh = false
  } = {}) {
    return Promise.all([this.collection.setServerFilter(this.query, {
      refresh
    }).catch(() => null), this.loadStatistics()]);
  }

  /** The statistics count the filtered correlations, which pages the list (as `$alerting.handleTotalCountLoad`). */
  applyTotals() {
    const {
      totalCount
    } = this.statistics.summary;
    const {
      perPage
    } = this.collection.pageState;
    this.collection.setPageState({
      totalCount,
      pageCount: Math.max(1, Math.ceil(totalCount / perPage)),
      pageCountLoaded: true
    });
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "filters", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return DEFAULT_CORRELATION_FILTERS;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "window", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return activeRange(DEFAULT_CORRELATION_FILTERS);
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "granularityValue", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "canAck", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canAck"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "canClear", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "canClear"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "granularity", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "granularity"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "query", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "query"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "createdRange", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "createdRange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "loadList", [action], Object.getOwnPropertyDescriptor(_class.prototype, "loadList"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setFilters", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setFilters"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "refresh", [action], Object.getOwnPropertyDescriptor(_class.prototype, "refresh"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setGranularity", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setGranularity"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "resetList", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetList"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "applyTotals", [action], Object.getOwnPropertyDescriptor(_class.prototype, "applyTotals"), _class.prototype), _class);
export default new CorrelationsStore();